import { BrokerCommunication } from "@dune/types";
import { formatSimpleDate, formatEntityReference } from "@dune/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "@dune/ui";
import { ColumnDef } from "@tanstack/react-table";
import {
	ArrowDownLeft,
	ArrowUpRight,
	Forward,
	Paperclip,
} from "lucide-react";

const CellWithTooltip = ({
	children,
	tooltip,
}: {
	children: React.ReactNode;
	tooltip: string;
}) => {
	if (!tooltip) {
		return <div className="truncate">{children}</div>;
	}

	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<div className="truncate">{children}</div>
			</TooltipTrigger>
			<TooltipContent align="start">{tooltip}</TooltipContent>
		</Tooltip>
	);
};

const getDirectionIconColor = (
	direction: string,
	isForwarded: boolean,
) => {
	if (isForwarded) return "text-indigo-500";
	if (direction === "INBOUND") return "text-emerald-500";
	return "text-amber-500";
};

export const communicationColumns: ColumnDef<BrokerCommunication>[] = [
	{
		id: "entityReference",
		header: "Référence",
		cell: ({ row }) => {
			const communication = row.original;
			if (
				!communication.relatedEntities ||
				communication.relatedEntities.length === 0
			) {
				return <div className="text-gray-500">-</div>;
			}

			const firstEntity = communication.relatedEntities[0];

			if (
				firstEntity.entityType === "PROPOSITION" &&
				firstEntity.entityReference === firstEntity.entityId
			) {
				return <div className="text-gray-500">-</div>;
			}

			const reference = formatEntityReference(
				firstEntity.entityType,
				firstEntity.entityReference,
			);

			return (
				<CellWithTooltip tooltip={reference}>
					<span className="font-medium text-gray-900">
						{reference}
					</span>
				</CellWithTooltip>
			);
		},
		size: 130,
	},
	{
		accessorKey: "subject",
		header: "Sujet",
		cell: ({ row }) => {
			const communication = row.original;
			const isForwarded = /^[Ff]wd:/i.test(
				communication.subject || "",
			);
			const iconColor = getDirectionIconColor(
				communication.direction || "OUTBOUND",
				isForwarded,
			);

			return (
				<div className="flex items-center overflow-hidden w-full min-w-0">
					{!communication.isProcessed && (
						<span className="w-2 h-2 bg-blue-500 rounded-full mr-2 flex-shrink-0" />
					)}
					{isForwarded ? (
						<Forward
							size={16}
							className={`${iconColor} mr-1 flex-shrink-0`}
							aria-label="Transféré"
						/>
					) : communication.direction === "INBOUND" ? (
						<ArrowDownLeft
							size={16}
							className={`${iconColor} mr-1 flex-shrink-0`}
							aria-label="Email reçu"
						/>
					) : (
						<ArrowUpRight
							size={16}
							className={`${iconColor} mr-1 flex-shrink-0`}
							aria-label="Email envoyé"
						/>
					)}
					<Tooltip>
						<TooltipTrigger asChild>
							<span className="truncate font-medium flex-1 min-w-0">
								{communication.subject || "Sans objet"}
							</span>
						</TooltipTrigger>
						<TooltipContent align="start">
							{communication.subject || "Sans objet"}
						</TooltipContent>
					</Tooltip>
					{communication.attachments &&
						communication.attachments.length > 0 && (
							<Paperclip
								size={16}
								className="ml-1 text-gray-500 flex-shrink-0"
							/>
						)}
				</div>
			);
		},
		enableSorting: true,
		sortingFn: (rowA, rowB) => {
			const subjectA = rowA.original.subject;
			const subjectB = rowB.original.subject;
			return subjectA.localeCompare(subjectB);
		},
	},
	{
		accessorKey: "from",
		header: "De",
		cell: ({ row }) => {
			const from = row.original.from;
			return (
				<CellWithTooltip tooltip={from}>
					<span>{from}</span>
				</CellWithTooltip>
			);
		},
		size: 200,
		enableSorting: true,
		sortingFn: (rowA, rowB) => {
			const nameA = rowA.original.from;
			const nameB = rowB.original.from;
			return nameA.localeCompare(nameB);
		},
	},
	{
		accessorKey: "to",
		header: "À",
		cell: ({ row }) => {
			const toList = row.original.to;
			const toText = toList.join(", ");
			return (
				<CellWithTooltip tooltip={toText}>
					<span>{toText}</span>
				</CellWithTooltip>
			);
		},
		size: 200,
		enableSorting: true,
		sortingFn: (rowA, rowB) => {
			const nameA = rowA.original.to.join(", ");
			const nameB = rowB.original.to.join(", ");
			return nameA.localeCompare(nameB);
		},
	},
	{
		accessorKey: "date",
		header: "Date",
		cell: ({ row }) => {
			return <span>{formatSimpleDate(row.original.date)}</span>;
		},
		size: 120,
		enableSorting: true,
		sortingFn: (rowA, rowB) => {
			const dateA = new Date(rowA.original.date);
			const dateB = new Date(rowB.original.date);
			return dateA.getTime() - dateB.getTime();
		},
	},
];
